import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { formatAmountForCopy } from "../utils/currency";
import { minPriceFor, minRequestPriceFor, type TierTalent } from "../utils/payinConfig";
import { t } from "../i18n";
import "./PriceFloorNote.css";

// The quiet "Minimum $2 (i)" line that sits on top of the talent's price
// number pad (founder, 2026-10-22). The amount is the talent's floor
// (minRequestPriceFor — the server's number for this talent, else the row);
// the (i) also names the tip minimum (minPriceFor, the $2 class) — founder
// wording 2026-10-30: "…so card fees take a smaller share. Tips start at $2,
// and free requests are always allowed." While the typed amount is under it the
// line turns amber; Done lifts the amount to the floor (utils/priceFloor).
// Tapping (i) opens a one-sentence explanation above the pad; any other tap
// closes it.

type Props = {
  currency?: string | null;
  /** The typed amount is a real amount under the floor right now. */
  below: boolean;
  /** This pad accepts 0 as "Free" — the explanation says so. */
  allowFree?: boolean;
  /** The talent's own profile (minRequestPrice from the server). */
  talent?: TierTalent;
};

export default function PriceFloorNote({ currency, below, allowFree = false, talent }: Props) {
  const [open, setOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLButtonElement>(null);
  const [arrowX, setArrowX] = useState<number | null>(null);
  const amount = formatAmountForCopy(minRequestPriceFor(currency, talent), currency);
  const tipAmount = formatAmountForCopy(minPriceFor(currency), currency);

  // Point the bubble's arrow at the (i), wherever the line's width puts it.
  useLayoutEffect(() => {
    if (!open || !rowRef.current || !infoRef.current) return;
    const row = rowRef.current.getBoundingClientRect();
    const info = infoRef.current.getBoundingClientRect();
    setArrowX(info.left + info.width / 2 - row.left);
  }, [open]);

  // Any tap outside the (i) and the bubble closes it (keys, backdrop, page).
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      const target = e.target as Node;
      if (infoRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", close, true);
    return () => document.removeEventListener("pointerdown", close, true);
  }, [open]);

  return (
    <div className="pfn" ref={rowRef}>
      <span className={`pfn-text${below ? " pfn-text--below" : ""}`}>
        {t("numberPad.minimum", { amount })}
      </span>
      <button
        type="button"
        ref={infoRef}
        className="pfn-info"
        aria-label={t("numberPad.aboutTheMinimum")}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
          <circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="10" cy="6.4" r="1.05" fill="currentColor" />
          <path d="M10 9.2v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div
          className="pfn-pop"
          role="tooltip"
          style={arrowX != null ? ({ "--pfn-arrow-x": `${arrowX}px` } as React.CSSProperties) : undefined}
        >
          {t(allowFree ? "numberPad.minimumWhyFree" : "numberPad.minimumWhy", { amount, tipAmount })}
        </div>
      )}
    </div>
  );
}
